import React from 'react';
import HomeLink from './HomeLink';
import { getHeroBannerSrc, getHeroBannerSrcSet } from '../../../utils/imageUrl';
import { ChatIcon, ChevronRightIcon, SearchIcon, SlidersIcon, TypeChipIcon } from './icons';
import {
  CONTENT_SHELL,
  DISPLAY_FONT,
  FOCUS_RING,
  FOCUS_RING_ON_BRAND,
  HERO_HEIGHT_CSS,
} from './tokens';
import { HOME_TYPE_CHIPS, chipIndexById } from './propertyTypes';

/**
 * HOME-01 — the hero, its search card, and the mobile search row.
 *
 * PROGRESSIVE ENHANCEMENT, on purpose.
 * The card is a real `<form method="get" action="/">`. On the server render —
 * which is what Google and a cold first visit see — submitting it navigates to
 * `/?locations=…&type=…`, the exact query string `hydrateFiltersFromUrl`
 * already parses (lib/search/urlState.ts). Once the SPA has hydrated, `onSubmit`
 * intercepts and runs the search in place, which is also what keeps the native
 * build from doing a document navigation and restarting its own shell.
 *
 * FIX ROUND 1 — THE FIELD IS NOW LIVE.
 * It was a real `<input>` bound to the page's `inputText` before, but that was
 * the whole of it: typing did nothing visible, because the autocomplete that
 * turns "Nair" into Nairobi / Nairobi CBD lives in the SmartSearch overlay and
 * nothing here opened it. So the primary control on the highest-traffic page
 * looked like a search box and behaved like a text field. `onOpenSearch` hands
 * focus and click to that overlay — the SAME overlay, prefilled from the same
 * `inputText`, with the same parser and the same "Show N Results" footer.
 * Nothing about SmartSearch is reimplemented here; this field is a door to it.
 *
 * FIX ROUND 1 — THE HERO NO LONGER STRETCHES.
 * The design was drawn on a fixed 1440 frame and this was built with its
 * gutters and no ceiling, so at 1920 and 2560 the two cards sat at the left
 * edge of an ever-widening field of green. Now: the BACKGROUND is full-bleed
 * (a photograph crops, which is what photographs are for), the CONTENT sits in
 * the same centred, capped shell as every other row on the page, and the height
 * is clamped so a short laptop viewport does not spend its whole fold here.
 *
 * THE HERO IMAGE.
 * The design calls for a bright Kenyan residential photograph. No such asset is
 * in this repo, and none of the data the page fetches is one — the alternatives
 * were a stock image we hold no licence to, or an agent's listing photo
 * promoted to site furniture without them agreeing. Both were refused. The hero
 * ships as a brand panel and `heroImageUrl` is plumbed through, so supplying
 * the photograph is a one-value change (HOME-03). The image layer is already
 * `object-fit: cover` over the clamped box, so the day it arrives it crops on
 * an ultrawide instead of stretching, with no further layout work.
 *
 * LCP. When a hero image does exist it is the largest contentful paint on the
 * highest-traffic page in the product, so it loads eager at high priority and
 * is never lazy. Until then the panel is a gradient, the H1 is the LCP element,
 * and the page has one fewer blocking image than it did before.
 */

/**
 * The chips, re-exported at the name this module has always used so the two
 * page shells keep one import.
 *
 * The taxonomy itself moved to ./propertyTypes — it is now shared with the
 * filter sheet and BOTH queries, which is what makes a chip mean the same rows
 * everywhere. The placeholder set that shipped in the first build named five
 * chips against a single-valued `PropertyType`, and "House / Villa" therefore
 * silently excluded every bungalow on the site.
 */
export { HOME_TYPE_CHIPS as TYPE_CHIPS } from './propertyTypes';

/** `1,642` — the design's formatting, locale-pinned like CURR-03's amounts. */
export function formatCount(count: number): string {
  return Math.max(0, Math.round(count)).toLocaleString('en-US');
}

export interface HeroSearchProps {
  /**
   * The live total, and the SAME number the All-properties counter shows — one
   * page may not print two totals for one catalogue. Null when the count could
   * not be reached: the proof card then makes no numeric claim at all rather
   * than printing a zero (fix round 1; see homeCounts.ts).
   */
  count: number | null;
  /** Controlled on the SPA; uncontrolled (plain form field) during SSR. */
  locationValue?: string;
  onLocationChange?: (value: string) => void;
  /** Chip id from ./propertyTypes. Defaults to All types. */
  activeChipId?: string;
  onPickChip?: (chipId: string) => void;
  /** Present → intercepts the form. Absent → the browser performs the GET. */
  onSubmit?: () => void;
  /**
   * Present → focusing or clicking the field opens the SmartSearch overlay,
   * carrying whatever has been typed. Absent (server render, pre-hydration) →
   * the field stays a plain form input and the GET still works.
   */
  onOpenSearch?: () => void;
  /** Present → "More filters" opens the sheet. Absent → it is not rendered. */
  onMoreFilters?: () => void;
  /** Real area photography, when it exists. */
  heroImageUrl?: string | null;
}

/**
 * HOME-03 — the hero is a CONTAINED banner, not a full-bleed band.
 *
 * It used to run to both screen edges. That was HOME-01 fix round 1's answer to
 * the hero stretching into an empty green field on wide monitors: clamp the
 * height, cap the content, and let the photograph use the extra pixels.
 *
 * Dirie's call after seeing it beside Hemnet reverses that, and it is the better
 * reading of the same problem. The hero was the ONE element on this page that
 * ignored `CONTENT_SHELL` — header, every discovery row, All properties and the
 * footer all stop at 1440, and the hero did not. Two white cards floating in an
 * ever-widening band is what "empty void" actually described, and containing the
 * band removes the cause rather than clamping a symptom. It also means the hero
 * has no width of its own to maintain: it is the same column as everything else.
 *
 * CONSEQUENCE FOR THE PHOTOGRAPH. The crop is now nearly fixed — roughly 2.3:1
 * at every desktop width — instead of sliding between 1.8:1 and 4.6:1 with the
 * viewport. A photograph can be composed for one crop; it cannot be composed for
 * all of them. See config/homeImages.ts for the source dimensions.
 *
 * The brand panel stays BEHIND the photograph rather than instead of it: it is
 * what shows while the image decodes and what shows if the image 404s, so the
 * hero is never a white hole.
 */
const HeroBackdrop: React.FC<{ heroImageUrl?: string | null }> = ({ heroImageUrl }) => (
  <div
    className="absolute inset-0 overflow-hidden rounded-2xl"
    style={{ background: 'linear-gradient(135deg, #134025 0%, #1B5533 48%, #2A6B44 100%)' }}
    aria-hidden="true"
  >
    {heroImageUrl && (
      <img
        src={getHeroBannerSrc(heroImageUrl)}
        srcSet={getHeroBannerSrcSet(heroImageUrl)}
        /*
          The banner is at most 1312 CSS px wide (1440 shell − 2×64 gutter), so
          2624 covers a 2× display and nothing larger is ever worth fetching.
          Without `sizes` the browser assumes 100vw and picks the largest
          candidate on every screen — which on this element, the page's LCP,
          is the difference between ~90 KB and ~400 KB.
        */
        sizes="(max-width: 1439px) calc(100vw - 128px), 1312px"
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="sync"
        className="h-full w-full object-cover"
      />
    )}
  </div>
);

/** Desktop-only. The mobile frame in the design has no hero. */
export const HomeHero: React.FC<HeroSearchProps> = ({
  count,
  locationValue,
  onLocationChange,
  activeChipId,
  onPickChip,
  onSubmit,
  onOpenSearch,
  onMoreFilters,
  heroImageUrl = null,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    if (!onSubmit) return; // No handler yet (pre-hydration) — let the GET happen.
    e.preventDefault();
    onSubmit();
  };

  const activeIndex = chipIndexById(activeChipId);

  /**
   * Focus AND click, because they are two different arrivals: Tab focuses
   * without clicking, and a click on an already-focused field fires no focus
   * event. Either one is the buyer saying "I want to search", and the overlay
   * is where searching happens.
   */
  const openSearch = onOpenSearch
    ? {
        onFocus: onOpenSearch,
        onClick: onOpenSearch,
      }
    : {};

  return (
    /*
      The banner sits in the same shell as every other row, so its left edge is
      the page's left edge. `relative` on the inner box makes it the positioning
      context for the backdrop; the cards are padded INSIDE it, the way Hemnet
      pads its search card inside its own banner.
    */
    <section className={`hidden pt-3 lg:block ${CONTENT_SHELL}`}>
      <div className="relative overflow-hidden rounded-2xl" style={{ height: HERO_HEIGHT_CSS }}>
        <HeroBackdrop heroImageUrl={heroImageUrl} />

        <div className="absolute inset-0 flex items-center gap-6 px-8 xl:px-12">
          <form
            method="get"
            action="/"
            onSubmit={handleSubmit}
            className="w-[460px] flex-none rounded-2xl bg-white px-7 pb-6 pt-7 shadow-[0_12px_40px_rgba(20,30,20,.25)]"
          >
            <h1
              className="m-0 mb-[18px] text-[34px] font-extrabold leading-[1.12] tracking-[-0.01em] text-[#134025]"
              style={{ fontFamily: DISPLAY_FONT }}
            >
              Find your home in Kenya.
            </h1>

            {/*
              The focus ring is on the LABEL because the input has no border of
              its own — the box the buyer sees is this element. `focus-within`
              rather than `focus-visible`: the target is a text field, and a
              field that gives no visible sign it has focus is the same defect
              whether the focus arrived by Tab or by tap.
            */}
            <label
              htmlFor="home-hero-search"
              className="mb-3.5 flex items-center gap-2.5 rounded-[10px] border-[1.5px] border-[#DCDAD3] px-3.5 py-3 focus-within:border-[#134025] focus-within:ring-2 focus-within:ring-[#134025]/30"
            >
              <SearchIcon size={17} stroke="#666666" className="flex-none" />
              <span className="sr-only">Search area, estate or county</span>
              {/*
                A real input, labelled and focusable — never a styled div.
                Controlled once the SPA supplies a handler, uncontrolled before
                then: React refuses both `value` and `defaultValue` on one
                input, and an input that is controlled with no onChange is
                read-only, so the two modes are spread in rather than both
                being present.
              */}
              <input
                id="home-hero-search"
                type="text"
                name="locations"
                {...(onLocationChange
                  ? {
                      value: locationValue ?? '',
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationChange(e.target.value),
                    }
                  : { defaultValue: '' })}
                {...openSearch}
                placeholder="Search area, estate or county"
                autoComplete="off"
                className="w-full border-none bg-transparent p-0 text-[14.5px] text-[#2D2D2D] outline-none placeholder:text-[#8C8C86]"
              />
            </label>

            {/* Six chips, 3×2. The set and the `property_type` values behind it
                are ./propertyTypes' — one definition, shared with the filter
                sheet and both halves of the feed query. */}
            <div className="mb-3 grid grid-cols-3 gap-2" role="group" aria-label="Property type">
              {HOME_TYPE_CHIPS.map((chip, index) => {
                const selected = index === activeIndex;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={onPickChip ? () => onPickChip(chip.id) : undefined}
                    aria-pressed={selected}
                    className={`flex items-center justify-center gap-[7px] rounded-[9px] border-[1.5px] px-1.5 py-2.5 text-[12.5px] font-semibold transition-colors ${FOCUS_RING} ${
                      selected
                        ? 'border-[#134025] bg-[#134025] text-white'
                        : 'border-[#DCDAD3] bg-white text-[#3D3D3D] hover:border-[#134025]'
                    }`}
                  >
                    <TypeChipIcon index={index} />
                    <span>{chip.label}</span>
                  </button>
                );
              })}
            </div>
            {/* Carries the chip choice for the no-JS GET, and harmless once hydrated. */}
            <input
              type="hidden"
              name="type"
              value={HOME_TYPE_CHIPS[activeIndex]?.id ?? 'all'}
            />

            {onMoreFilters && (
              <button
                type="button"
                onClick={onMoreFilters}
                className={`mb-4 inline-flex items-center gap-1 rounded text-[13px] font-semibold text-[#134025] hover:text-[#1B5533] hover:underline ${FOCUS_RING}`}
              >
                More filters
                <ChevronRightIcon size={12} />
              </button>
            )}

            <button
              type="submit"
              className={`w-full rounded-[10px] border-none bg-[#134025] px-4 py-[15px] text-[15.5px] font-bold tracking-[0.01em] text-white transition-colors hover:bg-[#1B5533] ${FOCUS_RING}`}
            >
              Find homes for sale
            </button>
          </form>

          <div className="w-[280px] flex-none self-center rounded-[14px] bg-white/95 px-[22px] py-5 shadow-[0_8px_28px_rgba(20,30,20,.18)]">
            <div
              className="mb-2 text-[17px] font-extrabold leading-[1.3] tracking-[-0.01em] text-[#134025]"
              style={{ fontFamily: DISPLAY_FONT }}
            >
              {/* No number rather than a wrong one: a count we could not reach
                  is not zero, and "0 homes for sale — one search." is the worst
                  sentence this page could print. */}
              {count === null
                ? 'Homes for sale across Kenya — one search.'
                : `${formatCount(count)} homes for sale — one search.`}
            </div>
            <div className="flex items-start gap-2">
              <ChatIcon size={16} stroke="#134025" className="mt-0.5 flex-none" />
              <div className="text-[13px] leading-[1.5] text-[#666666]">
                Message agents in-app. Your number stays private until you share it.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Mobile search row. The design's phone frame opens on this instead of a hero —
 * the search field, then straight into the rows.
 *
 * Both controls open the same overlay the hero field does, which is the whole
 * of "one search path on this page". The field is a `<button>` rather than an
 * `<input>` on purpose here: on a phone the overlay IS the search field (it
 * takes the whole screen and focuses its own input), so a second input would
 * open the keyboard against a box that is about to be replaced.
 *
 * IT FOLLOWS THE SCROLL (Dirie, on device).
 * The row used to scroll away with the page, so a buyer three screens into the
 * feed had no way to search without scrolling back to the top — on the one
 * surface whose entire purpose is search-first. It is `sticky` rather than
 * `fixed`: it starts in the flow where the design puts it, and only pins once
 * it reaches the navbar. Fixed would have taken it out of the flow immediately
 * and left a gap where the row had been.
 *
 * `top` is the navbar's full height INCLUDING the safe-area inset. That is the
 * house rule for chrome pinned below the navbar (see the comment in
 * components/Navbar.tsx): in-flow content stays `pt-16` because the body
 * already adds the inset, but a sticky element is positioned against the
 * viewport, where the navbar occupies `4rem + inset`. env() resolves to 0 on
 * web, so the web position is unchanged.
 *
 * `z-30` sits under the navbar (z-50) and under the search overlay (z-60), and
 * above the feed. The opaque background is load-bearing — cards scrolling
 * underneath a transparent bar is the defect this pattern is famous for.
 */
export const MobileSearchRow: React.FC<{
  count: number | null;
  onOpenSearch?: () => void;
  onMoreFilters?: () => void;
}> = ({ count, onOpenSearch, onMoreFilters }) => {
  const label =
    count === null ? 'Search homes for sale' : `Search ${formatCount(count)} homes for sale`;

  return (
    <div className="sticky top-[calc(4rem+env(safe-area-inset-top))] z-30 border-b border-[#E7E6E6] bg-[#F7F6F1] lg:hidden">
      <div className={`${CONTENT_SHELL} flex gap-2.5 pb-2 pt-2`}>
        {onOpenSearch ? (
          <button
            type="button"
            onClick={onOpenSearch}
            className={`flex min-h-[48px] flex-1 items-center gap-2.5 rounded-xl border-[1.5px] border-[#DCDAD3] bg-white px-3.5 py-3 text-left ${FOCUS_RING}`}
          >
            <SearchIcon size={16} stroke="#666666" className="flex-none" />
            <span className="truncate text-[14px] text-[#666666]">{label}</span>
          </button>
        ) : (
          // Pre-hydration / no-JS: a real link to the same page rather than a
          // button that does nothing until App.tsx arrives.
          <HomeLink
            to="/"
            className={`flex min-h-[48px] flex-1 items-center gap-2.5 rounded-xl border-[1.5px] border-[#DCDAD3] bg-white px-3.5 py-3 ${FOCUS_RING}`}
          >
            <SearchIcon size={16} stroke="#666666" className="flex-none" />
            <span className="truncate text-[14px] text-[#666666]">{label}</span>
          </HomeLink>
        )}

        {onMoreFilters && (
          <button
            type="button"
            onClick={onMoreFilters}
            aria-label="Open filters"
            className={`flex w-12 flex-none items-center justify-center rounded-xl bg-[#134025] ${FOCUS_RING_ON_BRAND}`}
          >
            <SlidersIcon size={18} stroke="#ffffff" />
          </button>
        )}
      </div>
    </div>
  );
};
